import { useCallback, useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { User } from '../types/User';
import { fetchCurrentUser } from '../api/users/fetchUsers';

const useUserId = () => {
    const session = useSession();
    const [userId, setUserId] = useState<string>();

    useEffect(() => {
        const fetchSession = async () => {
            try {
                const id = session?.data?.user?.id;
                id && setUserId(id);
            } catch (error) {
                console.error('Failed to fetch userIs', error);
            }
        };

        fetchSession();
    }, [session]);

    return userId;
};

/*
There are two states. State one - a session does not exist. If thats the case the session cannot be expired, because it does not even exist.
State two - a session exists. The session can either be valid or expired. 
This hook will return true, if the session exists, but is expired. It will return false if the session is valid or if the session does not exist.
*/
const useIsSessionExpired = (): boolean => {
    const session = useSession();
    const isSessionExpired =
        session?.data?.expires && new Date(session.data.expires).getTime() < Date.now();
    return isSessionExpired || false;
};

interface PopulateOptions {
    profilePicture?: { populate: true };
    institution?: { populate: true };
    favouriteCompanies?: { populate: true };
    favouriteProjects?: { populate: true };
}

const useUser = (options?: {
    withInstitution?: boolean;
    withProfilePicture?: boolean;
    withFavourites?: boolean;
}) => {
    const { data: session, status } = useSession();
    const [user, setUser] = useState<User>();
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const getUser = useCallback(async () => {
        if (status === 'authenticated' && session?.user?.id) {
            try {
                const populateOptions: PopulateOptions = {};

                if (options?.withProfilePicture) {
                    populateOptions.profilePicture = { populate: true };
                }

                if (options?.withInstitution) {
                    populateOptions.institution = { populate: true };
                }

                if (options?.withFavourites) {
                    populateOptions.favouriteCompanies = { populate: true };
                    populateOptions.favouriteProjects = { populate: true };
                }

                const { user } = await fetchCurrentUser({ populate: populateOptions });
                setUser(user);
            } catch (error) {
                console.error('Failed to fetch user', error);
            } finally {
                setIsLoading(false);
            }
        } else if (status === 'loading') {
            return;
        } else {
            setUser(undefined);
            setIsLoading(false);
        }
    }, [
        options?.withFavourites,
        options?.withInstitution,
        options?.withProfilePicture,
        session?.user?.id,
        status
    ]);

    useEffect(() => {
        getUser();
    }, [status, getUser]);

    return { user, isLoading, refreshUser: getUser };
};

export { useUserId, useUser, useIsSessionExpired };
